<template>
  <div class="photo-map">
    <div ref="mapContainer" class="map-container"></div>
    
    <!-- Photo Preview Tooltip -->
    <div 
      v-if="hoveredPhoto" 
      ref="photoPreview"
      class="photo-preview-tooltip"
      :style="previewPosition"
    >
      <div class="preview-image-container">
        <img 
          :src="getPhotoPreviewUrl(hoveredPhoto)" 
          :alt="hoveredPhoto.original_name"
          class="preview-image"
          @load="onPreviewImageLoad"
          @error="onPreviewImageError"
        />
        <div v-if="imageLoading" class="preview-loading">
          <div class="preview-spinner"></div>
        </div>
        <div v-if="imageError" class="preview-error">
          <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
        </div>
      </div>
      <div class="preview-info">
        <div class="preview-meta">
          <span v-if="hoveredPhoto.date_taken">{{ formatPreviewDate(hoveredPhoto.date_taken) }}</span>
          <span v-if="hoveredPhoto.tags && hoveredPhoto.tags.length > 0">
            {{ hoveredPhoto.tags.slice(0, 2).join(', ') }}
            <span v-if="hoveredPhoto.tags.length > 2">...</span>
          </span>
        </div>
      </div>
    </div>
    
    <div v-if="loading" class="map-loading">
      <div class="spinner"></div>
      <p>{{ $t('map.loading') }}</p>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster'
import { photosAPI } from '../services/api'

// Fix for default markers in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

export default {
  name: 'PhotoMap',
  props: {
    photos: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      markersLayer: null,
      loading: true,
      hoveredPhoto: null,
      previewPosition: {
        left: '0px',
        top: '0px'
      },
      imageLoading: false,
      imageError: false,
      hoverTimeout: null
    }
  },
  mounted() {
    this.initMap()
  },
  watch: {
    photos: {
      handler: 'updateMarkers',
      deep: true
    }
  },
  beforeUnmount() {
    this.cleanup()
  },
  methods: {
    initMap() {
      try {
        // Initialize map centered on Europe
        this.map = L.map(this.$refs.mapContainer, {
          zoomControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          touchZoom: true,
          dragging: true,
          minZoom: 2,
          maxZoom: 18,
          // Add these options to prevent animation issues
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true
        }).setView([50.1109, 8.6821], 6)

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 18
        }).addTo(this.map)

        // Create a marker cluster group with custom options
        this.markersLayer = L.markerClusterGroup({
          // Cluster options
          maxClusterRadius: 80, // Maximum radius a cluster will cover (in pixels)
          spiderfyOnMaxZoom: true, // When you click a cluster at max zoom, spiderfy it
          showCoverageOnHover: false, // Don't show cluster coverage on hover
          zoomToBoundsOnClick: true, // Zoom to cluster bounds when clicked
          disableClusteringAtZoom: 16, // Disable clustering at zoom level 16 and above
          
          // Animation options to prevent errors
          animate: true,
          animateAddingMarkers: false, // Disable adding animation to prevent timing issues
          
          // Custom cluster icon
          iconCreateFunction: function(cluster) {
            const count = cluster.getChildCount()
            let className = 'marker-cluster-small'
            
            if (count < 10) {
              className = 'marker-cluster-small'
            } else if (count < 100) {
              className = 'marker-cluster-medium'
            } else {
              className = 'marker-cluster-large'
            }
            
            return new L.DivIcon({
              html: `<div><span>${count}</span></div>`,
              className: `marker-cluster ${className}`,
              iconSize: new L.Point(40, 40)
            })
          }
        })

        // Add error handling for cluster group events
        this.markersLayer.on('animationend', () => {
          // Animation completed successfully
        })

        this.markersLayer.on('spiderfied', () => {
          // Spiderfy animation completed
        })

        // Add the cluster group to the map
        this.map.addLayer(this.markersLayer)

        // Add zoom event listener with error handling
        this.map.on('zoom', () => {
          try {
            // Handle zoom events safely
            if (this.hoveredPhoto) {
              this.hidePhotoPreview()
            }
          } catch (error) {
            console.warn('Error during zoom event:', error)
          }
        })

        this.loading = false
        this.updateMarkers()

      } catch (error) {
        console.error('Error initializing map:', error)
        this.loading = false
      }
    },

    updateMarkers() {
      // Ensure map and cluster group are properly initialized
      if (!this.map || !this.markersLayer || this.map._container === null) {
        console.warn('Map or cluster group not properly initialized')
        return
      }

      try {
        // Clear existing markers
        this.clearMarkers()

        // Add markers for photos with GPS data
        const photosWithGPS = this.photos.filter(photo => 
          photo.latitude && photo.longitude && 
          !isNaN(photo.latitude) && !isNaN(photo.longitude)
        )

        if (photosWithGPS.length === 0) {
          return
        }

        // Group photos by marker_group_id
        const photoGroups = new Map()
        
        photosWithGPS.forEach(photo => {
          const groupId = photo.marker_group_id || `single_${photo.id}`
          
          if (!photoGroups.has(groupId)) {
            photoGroups.set(groupId, [])
          }
          photoGroups.get(groupId).push(photo)
        })

        const validMarkers = []

        // Create one marker per group
        photoGroups.forEach((groupPhotos, groupId) => {
          try {
            // Use the first photo's coordinates for the marker position
            const representativePhoto = groupPhotos[0]
            const lat = parseFloat(representativePhoto.latitude)
            const lng = parseFloat(representativePhoto.longitude)

            // Validate coordinates
            if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
              console.warn('Invalid coordinates for photo group:', groupId, lat, lng)
              return
            }

            const marker = L.marker([lat, lng])

            // Add click event to emit photo selection for detail modal
            // Always emit the first photo of the group (representative photo)
            marker.on('click', () => {
              this.$emit('photo-selected', representativePhoto)
            })

            // Add hover events for photo preview (show representative photo)
            marker.on('mouseover', (e) => {
              this.showPhotoPreview(representativePhoto, e)
            })

            marker.on('mouseout', () => {
              this.hidePhotoPreview()
            })

            // Add marker to cluster group
            this.markersLayer.addLayer(marker)
            validMarkers.push(marker)

          } catch (error) {
            console.error('Error creating marker for photo group:', groupId, error)
          }
        })

        // Store valid markers
        this.markers = validMarkers

        // Fit map to show all markers using the cluster group
        if (validMarkers.length > 0 && this.map && this.markersLayer) {
          try {
            // Use the cluster group's bounds instead of creating a new feature group
            const bounds = this.markersLayer.getBounds()
            if (bounds && bounds.isValid()) {
              this.map.fitBounds(bounds.pad(0.1), {
                maxZoom: 15,
                animate: true,
                duration: 0.5
              })
            }
          } catch (error) {
            console.error('Error fitting bounds:', error)
            // Fallback to original method if cluster bounds fail
            try {
              const group = new L.featureGroup(validMarkers)
              const fallbackBounds = group.getBounds()
              if (fallbackBounds && fallbackBounds.isValid()) {
                this.map.fitBounds(fallbackBounds.pad(0.1), {
                  maxZoom: 15,
                  animate: true,
                  duration: 0.5
                })
              }
            } catch (fallbackError) {
              console.error('Fallback bounds fitting also failed:', fallbackError)
            }
          }
        }
      } catch (error) {
        console.error('Error updating markers:', error)
      }
    },

    clearMarkers() {
      if (!this.markersLayer) return

      try {
        // Remove all markers from the cluster group
        if (this.markersLayer && typeof this.markersLayer.clearLayers === 'function') {
          this.markersLayer.clearLayers()
        }
        
        // Clear the markers array with error handling
        this.markers.forEach(marker => {
          try {
            if (marker && typeof marker.remove === 'function') {
              marker.remove()
            }
          } catch (error) {
            // Ignore individual marker cleanup errors
            console.warn('Error removing individual marker:', error)
          }
        })
        
        this.markers = []
      } catch (error) {
        console.error('Error clearing markers:', error)
        // Reset markers array even if clearing failed
        this.markers = []
      }
    },

    cleanup() {
      try {
        // Clear hover timeout
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout)
          this.hoverTimeout = null
        }

        // Hide any active preview
        this.hoveredPhoto = null
        this.imageLoading = false
        this.imageError = false

        // Clear markers first
        this.clearMarkers()

        // Remove cluster group with proper error handling
        if (this.markersLayer && this.map) {
          try {
            // Remove event listeners first
            this.markersLayer.off()
            
            // Remove from map
            this.map.removeLayer(this.markersLayer)
            
            // Clear the cluster group
            this.markersLayer.clearLayers()
          } catch (clusterError) {
            console.warn('Error removing cluster layer:', clusterError)
          }
          this.markersLayer = null
        }

        // Remove map with error handling
        if (this.map) {
          try {
            // Remove all event listeners
            this.map.off()
            
            // Remove the map
            this.map.remove()
          } catch (mapError) {
            console.warn('Error removing map:', mapError)
          }
          this.map = null
        }

      } catch (error) {
        console.error('Error during cleanup:', error)
      }
    },

    getPhotoPreviewUrl(photo) {
      return photosAPI.getImage(photo.filename)
    },

    showPhotoPreview(photo, event) {
      // Clear any existing timeout
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
      }

      // Set a slight delay to prevent flickering
      this.hoverTimeout = setTimeout(() => {
        this.hoveredPhoto = photo
        this.imageLoading = true
        this.imageError = false

        // Position the preview near the marker
        this.$nextTick(() => {
          this.positionPreview(event)
        })
      }, 200)
    },

    hidePhotoPreview() {
      // Clear timeout if it exists
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }

      // Hide preview with a small delay to prevent flickering when moving between markers
      setTimeout(() => {
        if (!this.hoverTimeout) {
          this.hoveredPhoto = null
          this.imageLoading = false
          this.imageError = false
        }
      }, 100)
    },

    positionPreview(event) {
      if (!this.$refs.photoPreview || !event) return

      const mapContainer = this.$refs.mapContainer
      const preview = this.$refs.photoPreview
      const mapRect = mapContainer.getBoundingClientRect()
      
      // Get the container point from leaflet event
      const containerPoint = event.containerPoint
      
      // Calculate position relative to map container
      let left = containerPoint.x + 15 // Offset from cursor
      let top = containerPoint.y - 10

      // Get preview dimensions
      const previewRect = preview.getBoundingClientRect()
      const previewWidth = previewRect.width || 220 // Fallback width
      const previewHeight = previewRect.height || 280 // Fallback height

      // Adjust position to keep preview within map bounds
      if (left + previewWidth > mapRect.width) {
        left = containerPoint.x - previewWidth - 15 // Show on left side
      }

      if (top + previewHeight > mapRect.height) {
        top = mapRect.height - previewHeight - 10
      }

      if (top < 0) {
        top = 10
      }

      this.previewPosition = {
        left: `${left}px`,
        top: `${top}px`
      }
    },

    onPreviewImageLoad() {
      this.imageLoading = false
    },

    onPreviewImageError() {
      this.imageLoading = false
      this.imageError = true
    },

    formatPreviewDate(dateString) {
      if (!dateString) return ''
      
      let date
      // Handle EXIF date format (YYYY:MM:DD HH:MM:SS)
      if (typeof dateString === 'string' && dateString.includes(':')) {
        // Convert EXIF format to ISO format
        const isoString = dateString.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3')
        date = new Date(isoString)
      } else {
        date = new Date(dateString)
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return ''
      }
      
      // Use short date format
      return date.toLocaleDateString(this.$i18n?.locale === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.photo-map {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
}

.map-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  z-index: 1000;
}

.map-loading p {
  margin-top: 1rem;
  color: #666;
  font-weight: 500;
}

.photo-preview-tooltip {
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 0;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  z-index: 1001;
  max-width: 220px;
  overflow: hidden;
  pointer-events: none;
  transform: translateY(-5px);
  animation: fadeInUp 0.2s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(-5px);
  }
}

.preview-image-container {
  position: relative;
  width: 200px;
  height: 150px;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.preview-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
}

.preview-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-top: 2px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.preview-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  background: rgba(249, 250, 251, 0.9);
}

.preview-error svg {
  opacity: 0.5;
  margin-bottom: 0.5rem;
}

.preview-info {
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.9);
}

.preview-meta {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.3;
}

.preview-meta span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .photo-preview-tooltip {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .preview-image-container {
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  }

  .preview-info {
    background: rgba(17, 24, 39, 0.9);
  }

  .preview-meta {
    color: #9ca3af;
  }

  .preview-error {
    background: rgba(31, 41, 55, 0.9);
    color: #6b7280;
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .photo-preview-tooltip {
    max-width: 180px;
  }

  .preview-image-container {
    width: 160px;
    height: 120px;
  }

  .preview-info {
    padding: 0.5rem;
  }

  .preview-meta {
    font-size: 0.7rem;
  }
}

/* Custom Marker Cluster Styles */
.marker-cluster-small {
  background-color: rgba(99, 102, 241, 0.8);
  border: 2px solid rgba(99, 102, 241, 1);
}

.marker-cluster-small div {
  background-color: rgba(99, 102, 241, 0.9);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.marker-cluster-medium {
  background-color: rgba(139, 92, 246, 0.8);
  border: 2px solid rgba(139, 92, 246, 1);
}

.marker-cluster-medium div {
  background-color: rgba(139, 92, 246, 0.9);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.marker-cluster-large {
  background-color: rgba(168, 85, 247, 0.8);
  border: 2px solid rgba(168, 85, 247, 1);
}

.marker-cluster-large div {
  background-color: rgba(168, 85, 247, 0.9);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.marker-cluster {
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.marker-cluster:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.marker-cluster div span {
  color: white;
  font-weight: 700;
  font-size: 12px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Override default cluster styles */
.marker-cluster-small,
.marker-cluster-medium,
.marker-cluster-large {
  border-radius: 50% !important;
}

/* Spiderfy styles for when clusters are expanded */
.leaflet-cluster-anim .leaflet-marker-icon,
.leaflet-cluster-anim .leaflet-marker-shadow {
  transition: transform 0.3s ease-out, opacity 0.3s ease-in;
}

/* Dark mode support for clusters */
@media (prefers-color-scheme: dark) {
  .marker-cluster {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
  
  .marker-cluster:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
  }
}
</style> 